import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  operatorApi,
  type Tenant, type TenantUpdate, type TenantUser, type AccountSpec, type ProvisionedAccount,
  type UsersImportResult, type ImportResult,
} from '../../api/operator'
import { useAdminToken } from './AdminLayout'
import { rolesForModules } from '../../api/users'
import { Badge, type BadgeTone } from '../../components/Badge'
import AdminTenantScada from './AdminTenantScada'
import AdminEdgeDeployTools from './AdminEdgeDeployTools'

const MODULE_OPTIONS = [
  { value: 'meter_reading', label: 'Očitavanje vodomera' },
  { value: 'repair',        label: 'Popravke i servisi' },
  { value: 'scada',         label: 'SCADA nadzor' },
]

const MODULE_TONE: Record<string, BadgeTone> = {
  meter_reading: 'teal',
  repair:        'warning',
  scada:         'info',
}

function fmtDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('sr-Latn-RS', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

const inputCls = 'w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-hidden'

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">
        {label}{required && <span className="text-red-600 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  )
}

// ── Edit tenant modal ─────────────────────────────────────────────────────────

function EditTenantModal({ tenant, onClose }: { tenant: Tenant; onClose: () => void }) {
  const token = useAdminToken()
  const qc = useQueryClient()
  const [form, setForm] = useState<TenantUpdate>({
    name:               tenant.name,
    municipality:       tenant.municipality,
    contact_email:      tenant.contact_email ?? undefined,
    modules:            tenant.modules ?? [],
    seats:              tenant.seats,
    license_expires_at: tenant.license_expires_at ?? undefined,
    active:             tenant.active,
  })
  const [err, setErr] = useState('')

  const toggleModule = (m: string) =>
    setForm((f) => ({
      ...f,
      modules: f.modules?.includes(m) ? f.modules.filter((x) => x !== m) : [...(f.modules ?? []), m],
    }))

  const mutation = useMutation({
    mutationFn: () => operatorApi.updateTenant(token, tenant.id, form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['op-tenants'] })
      onClose()
    },
    onError: (e: unknown) =>
      setErr((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Greška pri izmeni.'),
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-1">Izmeni tenant</h2>
        <p className="text-xs text-gray-500 mb-5">{tenant.name} · {tenant.municipality}</p>

        <div className="space-y-4">
          <Field label="Naziv" required>
            <input type="text" value={form.name ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className={inputCls} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Opština">
              <input type="text" value={form.municipality ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, municipality: e.target.value }))}
                className={inputCls} />
            </Field>
            <Field label="Kontakt email">
              <input type="email" value={form.contact_email ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value || undefined }))}
                className={inputCls} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Broj sedišta">
              <input type="number" min={1} max={500} value={form.seats ?? tenant.seats}
                onChange={(e) => setForm((f) => ({ ...f, seats: Number(e.target.value) }))}
                className={inputCls} />
            </Field>
            <Field label="Licenca ističe">
              <input type="date"
                value={form.license_expires_at ? form.license_expires_at.split('T')[0] : ''}
                onChange={(e) => setForm((f) => ({ ...f, license_expires_at: e.target.value || undefined }))}
                className={inputCls} />
            </Field>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Moduli</label>
            <div className="flex flex-col gap-2">
              {MODULE_OPTIONS.map(({ value, label }) => (
                <label key={value} className="flex items-center gap-2.5 cursor-pointer select-none">
                  <input type="checkbox" checked={form.modules?.includes(value) ?? false}
                    onChange={() => toggleModule(value)}
                    className="rounded-sm text-brand-cyan focus:ring-blue-500 border-gray-300" />
                  <span className="text-sm text-gray-700">{label}</span>
                </label>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <input type="checkbox" checked={form.active ?? tenant.active}
              onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
              className="rounded-sm text-brand-cyan focus:ring-blue-500 border-gray-300" />
            <span className="text-sm text-gray-700">Tenant aktivan</span>
          </label>
        </div>

        {err && <p className="mt-3 text-xs text-red-600">{err}</p>}

        <div className="flex gap-2 mt-6">
          <button onClick={() => { setErr(''); mutation.mutate() }} disabled={mutation.isPending}
            className="flex-1 bg-brand-cyan hover:bg-brand-cyan-dark text-brand-midnight text-sm font-medium py-2.5 rounded-lg disabled:opacity-50">
            {mutation.isPending ? 'Čuvanje…' : 'Sačuvati izmene'}
          </button>
          <button onClick={onClose}
            className="flex-1 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium py-2.5 rounded-lg">
            Otkazati
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Revoke sessions ────────────────────────────────────────────────────────────
//   docs/plan-security-hardening-1.md §2's admin-panel revocation hook --
//   no modal, matches the low-risk/reversible nature of the action (the
//   user just logs back in); inline success text instead of a confirm step.

function RevokeSessionsButton({ tenant, user }: { tenant: Tenant; user: TenantUser }) {
  const token = useAdminToken()
  const [done, setDone] = useState(false)
  const mut = useMutation({
    mutationFn: () => operatorApi.revokeSessions(token, tenant.id, user.id),
    onSuccess: () => {
      setDone(true)
      setTimeout(() => setDone(false), 2000)
    },
  })

  return (
    <button
      onClick={() => mut.mutate()}
      disabled={mut.isPending}
      className="text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 px-2 py-1.5 rounded-lg transition-colors disabled:opacity-50"
    >
      {done ? 'Odjavljeno' : mut.isPending ? 'Odjavljivanje…' : 'Odjavi sesije'}
    </button>
  )
}

// ── Block / unblock ───────────────────────────────────────────────────────────
//   docs/plan-user-blocking.md's admin-panel UI. Errors surfaced inline
//   (director/last-admin/self guards can all fail) rather than silently
//   swallowed like RevokeSessionsButton's low-risk action above.

function BlockToggleButton({ tenant, user }: { tenant: Tenant; user: TenantUser }) {
  const token = useAdminToken()
  const qc = useQueryClient()
  const [err, setErr] = useState('')
  const mut = useMutation({
    mutationFn: () =>
      user.is_blocked
        ? operatorApi.unblockUser(token, tenant.id, user.id)
        : operatorApi.blockUser(token, tenant.id, user.id),
    onSuccess: () => {
      setErr('')
      qc.invalidateQueries({ queryKey: ['op-users', tenant.id, token] })
    },
    onError: (e: unknown) =>
      setErr((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Greška.'),
  })

  return (
    <div className="inline-block">
      <button
        onClick={() => mut.mutate()}
        disabled={mut.isPending}
        className={`text-xs font-medium px-2 py-1.5 rounded-lg transition-colors disabled:opacity-50 ${
          user.is_blocked
            ? 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50'
            : 'text-red-600 hover:text-red-700 hover:bg-red-50'
        }`}
      >
        {mut.isPending ? '…' : user.is_blocked ? 'Odblokiraj' : 'Blokiraj'}
      </button>
      {err && <p className="text-xs text-red-600 mt-1 max-w-[160px]">{err}</p>}
    </div>
  )
}

// ── Director flag ─────────────────────────────────────────────────────────────
//   docs/plan-security-hardening-1.md §3b. Only I-SOFT sets or clears this --
//   no equivalent tenant-side action exists. Not gated on activated_at (unlike
//   the buttons above): I-SOFT marks a tenant's pending director before they
//   ever activate, per the plan's "no automatic backfill" design.

function DirectorToggleButton({ tenant, user }: { tenant: Tenant; user: TenantUser }) {
  const token = useAdminToken()
  const qc = useQueryClient()
  const [err, setErr] = useState('')
  const mut = useMutation({
    mutationFn: () => operatorApi.setDirector(token, tenant.id, user.id, !user.is_director),
    onSuccess: () => {
      setErr('')
      qc.invalidateQueries({ queryKey: ['op-users', tenant.id, token] })
    },
    onError: (e: unknown) =>
      setErr((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Greška.'),
  })

  return (
    <div className="inline-block">
      <button
        onClick={() => mut.mutate()}
        disabled={mut.isPending}
        className={`text-xs font-medium px-2 py-1.5 rounded-lg transition-colors disabled:opacity-50 ${
          user.is_director
            ? 'text-amber-600 hover:text-amber-700 hover:bg-amber-50'
            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
        }`}
      >
        {mut.isPending ? '…' : user.is_director ? 'Ukloni direktora' : 'Postavi za direktora'}
      </button>
      {err && <p className="text-xs text-red-600 mt-1 max-w-[160px]">{err}</p>}
    </div>
  )
}

// ── Reset password modal ──────────────────────────────────────────────────────

function ResetPasswordModal({ tenant, user, onClose }: { tenant: Tenant; user: TenantUser; onClose: () => void }) {
  const token = useAdminToken()
  const [pw, setPw]   = useState('')
  const [err, setErr] = useState('')
  const [ok, setOk]   = useState(false)

  const mutation = useMutation({
    mutationFn: () => operatorApi.resetPassword(token, tenant.id, user.id, pw),
    onSuccess: () => setOk(true),
    onError: (e: unknown) =>
      setErr((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Greška.'),
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Reset lozinke</h2>
          <p className="text-xs text-gray-500 mt-0.5">{user.email ?? 'Neaktivan nalog'} · {tenant.name}</p>
        </div>

        {ok ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
              <svg className="w-5 h-5 text-emerald-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm font-semibold text-emerald-800">Lozinka je resetovana.</p>
            </div>
            <button onClick={onClose}
              className="w-full bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium py-2.5 rounded-lg">
              Zatvoriti
            </button>
          </div>
        ) : (
          <>
            <Field label="Nova lozinka">
              <input type="password" value={pw} onChange={(e) => setPw(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && pw.length >= 8 && mutation.mutate()}
                placeholder="Minimum 8 karaktera" className={inputCls} />
            </Field>
            {err && <p className="text-xs text-red-600">{err}</p>}
            <div className="flex gap-2">
              <button onClick={() => { setErr(''); mutation.mutate() }}
                disabled={mutation.isPending || pw.length < 8}
                className="flex-1 bg-brand-cyan hover:bg-brand-cyan-dark text-brand-midnight text-sm font-semibold py-2.5 rounded-lg disabled:opacity-50">
                {mutation.isPending ? 'Resetovanje…' : 'Resetovati'}
              </button>
              <button onClick={onClose}
                className="flex-1 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium py-2.5 rounded-lg">
                Otkazati
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ── Devices modal ─────────────────────────────────────────────────────────────

function DevicesModal({ tenant, user, onClose }: { tenant: Tenant; user: TenantUser; onClose: () => void }) {
  const token = useAdminToken()
  const { data: devices = [], isLoading } = useQuery({
    queryKey: ['op-user-devices', tenant.id, user.id, token],
    queryFn:  () => operatorApi.listUserDevices(token, tenant.id, user.id).then((r) => r.data),
    enabled:  !!tenant.id && !!user.id && !!token,
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Uređaji</h2>
          <p className="text-xs text-gray-500 mt-0.5">{user.email ?? 'Neaktivan nalog'} · {tenant.name}</p>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="w-6 h-6 border-4 border-brand-cyan border-t-transparent rounded-full animate-spin" />
          </div>
        ) : devices.length === 0 ? (
          <p className="text-sm text-gray-500 py-4">Nema registrovanih uređaja.</p>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {devices.map((d) => (
              <div key={d.id} className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                <p className="text-sm text-gray-900 font-medium">{d.device_name ?? 'Nepoznat uređaj'}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {d.platform ?? '—'}{d.app_version ? ` · v${d.app_version}` : ''}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Poslednja aktivnost: {d.last_sync_at ? fmtDate(d.last_sync_at) : 'nikad'}
                </p>
              </div>
            ))}
          </div>
        )}

        <button onClick={onClose}
          className="w-full bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium py-2.5 rounded-lg">
          Zatvoriti
        </button>
      </div>
    </div>
  )
}

// ── Extend token dialog ───────────────────────────────────────────────────────

function ExtendTokenDialog({ tenant, user, onClose }: { tenant: Tenant; user: TenantUser; onClose: () => void }) {
  const token = useAdminToken()
  const [copied, setCopied] = useState(false)
  const [err, setErr]       = useState('')

  const mutation = useMutation({
    mutationFn: () => operatorApi.extendToken(token, tenant.id, user.id),
    onError: (e: unknown) =>
      setErr((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Greška.'),
  })

  const webLink = mutation.data
    ? `${window.location.origin}/activate?token=${mutation.data.data.activation_token}`
    : null

  const handleCopy = () => {
    if (!webLink) return
    navigator.clipboard.writeText(webLink)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Produži aktivacioni link</h2>
          <p className="text-xs text-gray-500 mt-0.5">{user.email ?? user.full_name ?? 'Neaktivan nalog'} · {tenant.name}</p>
        </div>

        {!mutation.data && !mutation.isPending && (
          <p className="text-sm text-gray-500">
            Generisaće se novi aktivacioni link (važeći godinu dana). Stari link postaje nevažeći.
          </p>
        )}

        {mutation.isPending && (
          <div className="flex justify-center py-4">
            <div className="w-6 h-6 border-4 border-brand-cyan border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {webLink && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
              <svg className="w-5 h-5 text-emerald-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm font-semibold text-emerald-800">Novi link generisan</p>
            </div>
            <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
              <p className="text-xs font-mono text-gray-600 break-all flex-1">{webLink}</p>
              <button onClick={handleCopy} title="Kopirati link"
                className="shrink-0 text-gray-400 hover:text-gray-700 transition-colors">
                {copied
                  ? <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  : <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                }
              </button>
            </div>
          </div>
        )}

        {err && <p className="text-xs text-red-600">{err}</p>}

        <div className="flex gap-2">
          {!mutation.data && (
            <button onClick={() => { setErr(''); mutation.mutate() }} disabled={mutation.isPending}
              className="flex-1 bg-brand-cyan hover:bg-brand-cyan-dark text-brand-midnight text-sm font-semibold py-2.5 rounded-lg disabled:opacity-50">
              Generisati link
            </button>
          )}
          <button onClick={onClose}
            className="flex-1 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium py-2.5 rounded-lg">
            {mutation.data ? 'Zatvoriti' : 'Otkazati'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Provision modal ───────────────────────────────────────────────────────────

function ProvisionModal({ tenant, onClose }: { tenant: Tenant; onClose: () => void }) {
  const token = useAdminToken()
  const [specs, setSpecs]   = useState<AccountSpec[]>([{ roles: ['company_admin'], label: 'Admin' }])
  const [result, setResult] = useState<ProvisionedAccount[] | null>(null)
  const [err, setErr]       = useState('')

  const mutation = useMutation({
    mutationFn: () => operatorApi.provisionAccounts(token, tenant.id, specs),
    onSuccess: (r) => setResult(r.data),
    onError: (e: unknown) =>
      setErr((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Greška.'),
  })

  const addSpec    = () => setSpecs((s) => [...s, { roles: ['meter_reader'], label: '' }])
  const removeSpec = (i: number) => setSpecs((s) => s.filter((_, idx) => idx !== i))
  const updateSpec = (i: number, field: keyof AccountSpec, val: string | string[]) =>
    setSpecs((s) => s.map((sp, idx) => idx === i ? { ...sp, [field]: val } : sp))
  const toggleRole = (i: number, role: string) =>
    setSpecs((s) => s.map((sp, idx) => {
      if (idx !== i) return sp
      const roles = sp.roles.includes(role) ? sp.roles.filter((r) => r !== role) : [...sp.roles, role]
      return { ...sp, roles }
    }))

  const handleDownload = () => {
    const url = operatorApi.qrBundleUrl(tenant.id, token)
    const a = document.createElement('a')
    a.href = url; a.target = '_blank'; a.rel = 'noreferrer'
    document.body.appendChild(a); a.click(); a.remove()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-base font-semibold text-gray-900 mb-1">Provajdovati naloge</h2>
        <p className="text-xs text-gray-500 mb-5">{tenant.name} · {tenant.municipality}</p>

        {result ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
              <svg className="w-5 h-5 text-emerald-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm font-semibold text-emerald-800">
                {result.length} nalog{result.length > 1 ? 'a kreirana' : ' kreiran'}
              </p>
            </div>

            <div className="space-y-2">
              {result.map((acc, i) => {
                const webLink = `${window.location.origin}/activate?token=${acc.activation_token}`
                return (
                  <div key={acc.user_id} className="border border-gray-200 rounded-xl p-3 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-900">{acc.label ?? `Nalog ${i + 1}`}</span>
                      <span className="text-xs text-gray-500">{acc.roles.join(', ')}</span>
                    </div>
                    <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1.5">
                      <p className="text-xs font-mono text-gray-600 break-all flex-1">{webLink}</p>
                      <button onClick={() => navigator.clipboard.writeText(webLink)} title="Kopirati"
                        className="shrink-0 text-gray-400 hover:text-gray-700">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>

            <button onClick={handleDownload}
              className="w-full flex items-center justify-center gap-2 bg-brand-cyan hover:bg-brand-cyan-dark text-brand-midnight text-sm font-semibold py-2.5 rounded-lg">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Preuzeti QR bundle (PDF)
            </button>
            <button onClick={onClose}
              className="w-full bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium py-2 rounded-lg">
              Zatvoriti
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-xs text-gray-500">Svaki nalog dobija QR kod i link za aktivaciju.</p>

            {specs.map((spec, i) => (
              <div key={i} className="border border-gray-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-gray-700">Nalog {i + 1}</p>
                  {specs.length > 1 && (
                    <button onClick={() => removeSpec(i)} className="text-xs text-red-500 hover:text-red-700">Ukloniti</button>
                  )}
                </div>
                <Field label="Opis (opciono)">
                  <input type="text" value={spec.label ?? ''} onChange={(e) => updateSpec(i, 'label', e.target.value)}
                    placeholder="npr. Meračar za Zonu A" className={inputCls} />
                </Field>
                <div>
                  <label className="block text-xs text-gray-700 mb-1.5">Role</label>
                  <div className="flex flex-wrap gap-1.5">
                    {rolesForModules(tenant.modules ?? []).map(({ value, label }) => {
                      const active = spec.roles.includes(value)
                      return (
                        <button key={value} onClick={() => toggleRole(i, value)}
                          className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
                            active
                              ? 'bg-brand-cyan text-brand-midnight border-brand-cyan'
                              : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
                          }`}>
                          {label}
                        </button>
                      )
                    })}
                  </div>
                </div>
              </div>
            ))}

            <button onClick={addSpec}
              className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-gray-300 hover:border-blue-400 text-gray-500 hover:text-brand-cyan-text rounded-xl py-2.5 text-sm font-medium transition-colors">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              Dodati nalog
            </button>

            {err && <p className="text-xs text-red-600">{err}</p>}

            <div className="flex gap-2">
              <button onClick={() => { setErr(''); mutation.mutate() }}
                disabled={mutation.isPending || specs.some((s) => s.roles.length === 0)}
                className="flex-1 bg-brand-cyan hover:bg-brand-cyan-dark text-brand-midnight text-sm font-semibold py-2.5 rounded-lg disabled:opacity-50">
                {mutation.isPending ? 'Kreiranje…' : `Kreirati ${specs.length} nalog${specs.length > 1 ? 'a' : ''}`}
              </button>
              <button onClick={onClose}
                className="flex-1 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium py-2.5 rounded-lg">
                Otkazati
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Delete tenant modal ───────────────────────────────────────────────────────

function DeleteTenantModal({ tenant, onClose }: { tenant: Tenant; onClose: () => void }) {
  const token   = useAdminToken()
  const navigate = useNavigate()
  const qc       = useQueryClient()
  const [confirm, setConfirm] = useState('')
  const [err, setErr]         = useState('')

  const mutation = useMutation({
    mutationFn: () => operatorApi.deleteTenant(token, tenant.id, confirm),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['op-tenants'] })
      navigate('/admin/tenants', { replace: true })
    },
    onError: (e: unknown) =>
      setErr((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Greška pri brisanju.'),
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white border border-red-200 rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Obrisati tenant</h2>
          <p className="text-xs text-gray-500 mt-1">Ova akcija je nepovratna. Svi podaci tenanta biće trajno obrisani.</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-800">
          Unesite tačan naziv: <span className="font-mono font-semibold">{tenant.name}</span>
        </div>
        <Field label="Potvrda naziva">
          <input type="text" value={confirm} onChange={(e) => setConfirm(e.target.value)}
            placeholder={tenant.name} className={inputCls} />
        </Field>
        {err && <p className="text-xs text-red-600">{err}</p>}
        <div className="flex gap-2">
          <button onClick={() => { setErr(''); mutation.mutate() }}
            disabled={mutation.isPending || confirm !== tenant.name}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold py-2.5 rounded-lg disabled:opacity-50">
            {mutation.isPending ? 'Brisanje…' : 'Obrisati'}
          </button>
          <button onClick={onClose}
            className="flex-1 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium py-2.5 rounded-lg">
            Otkazati
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Info row ──────────────────────────────────────────────────────────────────

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start gap-4 py-3 border-b border-gray-100 last:border-0">
      <span className="text-xs text-gray-500 w-36 shrink-0 pt-0.5">{label}</span>
      <span className="text-sm text-gray-900 flex-1">{value ?? '—'}</span>
    </div>
  )
}

// ── CSV Import Card ───────────────────────────────────────────────────────────

type CardResult = UsersImportResult | ImportResult | null

function CsvImportCard({
  title, description, templateFile,
  onImport, showAccounts = false,
}: {
  title: string
  description: string
  templateFile: string
  onImport: (file: File) => Promise<{ data: CardResult }>
  showAccounts?: boolean
}) {
  const [file, setFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<CardResult>(null)
  const [err, setErr] = useState('')

  const handle = async () => {
    if (!file) return
    setLoading(true); setErr(''); setResult(null)
    try {
      const res = await onImport(file)
      setResult(res.data)
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setErr(detail ?? 'Greška pri uvozu')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-gray-900">{title}</p>
          <p className="text-xs text-gray-500 mt-0.5">{description}</p>
        </div>
        <a
          href={`/templates/${templateFile}`}
          download
          className="shrink-0 text-xs text-brand-cyan-text hover:text-brand-midnight underline underline-offset-2"
        >
          Predložak
        </a>
      </div>

      <div className="flex items-center gap-2">
        <label className="flex-1 flex items-center gap-2 bg-white border border-gray-300 rounded-lg px-3 py-2 cursor-pointer hover:border-gray-400 transition-colors">
          <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
          </svg>
          <span className="text-xs text-gray-500 truncate">{file ? file.name : 'Odaberite CSV fajl…'}</span>
          <input type="file" accept=".csv" className="sr-only"
            onChange={(e) => { setResult(null); setErr(''); setFile(e.target.files?.[0] ?? null) }} />
        </label>
        <button
          onClick={handle}
          disabled={!file || loading}
          className="shrink-0 text-sm font-medium bg-brand-cyan hover:bg-brand-cyan-dark disabled:opacity-50 text-brand-midnight px-4 py-2 rounded-lg transition-colors"
        >
          {loading ? 'Uvoz…' : 'Uvezi'}
        </button>
      </div>

      {err && <p className="text-xs text-red-600">{err}</p>}

      {result && (
        <div className="space-y-2">
          <p className="text-xs text-gray-700">
            <span className="text-emerald-600 font-semibold">{result.imported} uvezeno</span>
            {result.skipped > 0 && <span className="text-gray-500 ml-2">{result.skipped} preskočeno</span>}
          </p>
          {result.errors.length > 0 && (
            <ul className="space-y-0.5 max-h-32 overflow-y-auto">
              {result.errors.map((e, i) => (
                <li key={i} className="text-xs text-red-600">Red {e.row}: {e.message}</li>
              ))}
            </ul>
          )}
          {showAccounts && 'accounts' in result && result.accounts.length > 0 && (
            <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Aktivacioni linkovi</p>
              {result.accounts.map((a) => (
                <div key={a.email} className="flex items-center gap-2 text-xs bg-white border border-gray-200 rounded-sm px-2 py-1">
                  <span className="text-gray-700 w-40 truncate shrink-0">{a.email}</span>
                  <button
                    onClick={() => navigator.clipboard.writeText(`${window.location.origin}/activate?token=${a.activation_token}`)}
                    className="text-brand-cyan-text hover:text-brand-midnight underline underline-offset-2 truncate"
                    title="Kopirati link"
                  >
                    Kopiraj link
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}


// ── notify_scada toggle cell ──────────────────────────────────────────────────

function NotifyToggle({ tenant, user }: { tenant: Tenant; user: TenantUser }) {
  const token = useAdminToken()
  const qc    = useQueryClient()
  const mut   = useMutation({
    mutationFn: (enabled: boolean) =>
      operatorApi.setUserNotifyScada(token, tenant.id, user.id, enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['op-users', tenant.id, token] }),
  })
  return (
    <button
      onClick={() => mut.mutate(!user.notify_scada)}
      disabled={mut.isPending}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
        user.notify_scada ? 'bg-emerald-600' : 'bg-gray-300'
      } ${mut.isPending ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
        user.notify_scada ? 'translate-x-4' : 'translate-x-0.5'
      }`} />
    </button>
  )
}


// ── Main page ─────────────────────────────────────────────────────────────────

export default function AdminTenantDetail() {
  const { id }   = useParams<{ id: string }>()
  const token   = useAdminToken()
  const navigate = useNavigate()

  const { data: tenants = [], isLoading: loadingTenants } = useQuery({
    queryKey: ['op-tenants', token],
    queryFn: () => operatorApi.listTenants(token).then((r) => r.data),
    staleTime: 60_000,
  })

  const tenant = tenants.find((t) => t.id === id)

  const { data: stats } = useQuery({
    queryKey: ['op-stats', id, token],
    queryFn: () => operatorApi.tenantStats(token, id!).then((r) => r.data),
    enabled: !!id && !!token,
    staleTime: 30_000,
  })

  const { data: users = [], isLoading: loadingUsers, refetch: refetchUsers } = useQuery({
    queryKey: ['op-users', id, token],
    queryFn: () => operatorApi.listUsers(token, id!).then((r) => r.data),
    enabled: !!id && !!token,
    staleTime: 30_000,
  })

  const [editing,       setEditing]       = useState(false)
  const [provisioning,  setProvisioning]  = useState(false)
  const [resetting,     setResetting]     = useState<TenantUser | null>(null)
  const [extending,     setExtending]     = useState<TenantUser | null>(null)
  const [viewingDevices, setViewingDevices] = useState<TenantUser | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  if (loadingTenants) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-7 h-7 border-4 border-brand-cyan border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (!tenant) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-500">Tenant nije pronađen.</p>
        <button onClick={() => navigate('/admin/tenants')} className="mt-4 text-sm text-brand-cyan-text hover:text-brand-midnight">
          ← Nazad na listu
        </button>
      </div>
    )
  }

  const pending = users.filter((u) => !u.activated_at)

  return (
    <div className="p-8 max-w-4xl mx-auto space-y-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm">
        <button onClick={() => navigate('/admin/tenants')} className="text-gray-500 hover:text-gray-700 transition-colors">
          Tenanti
        </button>
        <span className="text-gray-300">/</span>
        <span className="text-gray-900 font-medium">{tenant.name}</span>
      </div>

      {/* Stats bar */}
      {stats && (
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: 'Ukupno naloga', value: stats.users_total },
            { label: 'Aktivirani',    value: stats.users_activated,                        accent: 'text-emerald-600' },
            { label: 'Na čekanju',    value: stats.users_total - stats.users_activated,    accent: stats.users_total - stats.users_activated > 0 ? 'text-amber-600' : undefined },
            { label: 'Sedišta',       value: `${stats.seats_used} / ${stats.seats_total}`, accent: stats.seats_used >= stats.seats_total ? 'text-red-600' : undefined },
          ].map(({ label, value, accent }) => (
            <div key={label} className="bg-white border border-gray-200 rounded-xl p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{label}</p>
              <p className={`text-2xl font-bold ${accent ?? 'text-gray-900'}`}>{value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Section A — Informacije */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900">Informacije</h2>
          <button onClick={() => setEditing(true)}
            className="text-xs font-medium text-brand-cyan-text hover:text-brand-midnight hover:bg-blue-50 px-2.5 py-1.5 rounded-lg transition-colors">
            Izmeni
          </button>
        </div>
        <div className="px-6 py-2">
          <InfoRow label="Naziv" value={tenant.name} />
          <InfoRow label="Opština" value={tenant.municipality} />
          <InfoRow label="Kontakt email" value={tenant.contact_email} />
          <InfoRow label="Kreiran" value={fmtDate(tenant.created_at)} />
          <InfoRow label="Licenca izdata" value={fmtDate(tenant.license_issued_at)} />
          <InfoRow label="Licenca ističe" value={fmtDate(tenant.license_expires_at)} />
          <InfoRow label="Sedišta" value={tenant.seats} />
          <InfoRow label="Status" value={
            <Badge tone={tenant.active ? 'success' : 'critical'}>
              {tenant.active ? 'Aktivan' : 'Neaktivan'}
            </Badge>
          } />
          <InfoRow label="Moduli" value={
            (tenant.modules ?? []).length === 0 ? '—' : (
              <div className="flex flex-wrap gap-1">
                {(tenant.modules ?? []).map((m) => (
                  <Badge key={m} tone={MODULE_TONE[m] ?? 'neutral'}>
                    {MODULE_OPTIONS.find((o) => o.value === m)?.label ?? m}
                  </Badge>
                ))}
              </div>
            )
          } />
        </div>
      </div>

      {/* Section B — Korisnici */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-gray-900">Korisnici</h2>
          <p className="text-xs text-gray-500 mt-0.5">Administrativni pregled naloga — bez ličnih podataka korisnika</p>
        </div>

        {loadingUsers ? (
          <div className="flex justify-center py-8">
            <div className="w-6 h-6 border-4 border-brand-cyan border-t-transparent rounded-full animate-spin" />
          </div>
        ) : users.length === 0 ? (
          <div className="py-10 text-center">
            <p className="text-sm text-gray-500">Nema naloga. Provajdujte naloge ispod.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                {['Status', 'Email / nalog', 'Role', 'Aktiviran', 'SCADA alert', 'Akcije'].map((h) => (
                  <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-5 py-3">
                    <div className={`w-2 h-2 rounded-full ${u.activated_at ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1.5">
                      <p className="text-gray-900 font-medium">{u.full_name ?? (u.email ?? '—')}</p>
                      {u.is_director && <Badge tone="warning">Direktor</Badge>}
                      {u.is_blocked && <Badge tone="critical">Blokiran</Badge>}
                    </div>
                    {u.full_name && <p className="text-xs text-gray-500">{u.email ?? 'Nije aktiviran'}</p>}
                    {!u.full_name && !u.email && <p className="text-xs text-gray-400">Nije aktiviran</p>}
                  </td>
                  <td className="px-5 py-3 text-gray-500 text-xs">{u.roles.join(', ')}</td>
                  <td className="px-5 py-3 text-gray-500 text-xs whitespace-nowrap">
                    {u.activated_at ? fmtDate(u.activated_at) : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-5 py-3">
                    <NotifyToggle tenant={tenant} user={u} />
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1">
                      {u.activated_at && (
                        <button onClick={() => setResetting(u)}
                          className="text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 px-2 py-1.5 rounded-lg transition-colors">
                          Reset lozinke
                        </button>
                      )}
                      {u.activated_at && (
                        <button onClick={() => setViewingDevices(u)}
                          className="text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 px-2 py-1.5 rounded-lg transition-colors">
                          Uređaji
                        </button>
                      )}
                      {u.activated_at && <RevokeSessionsButton tenant={tenant} user={u} />}
                      {u.activated_at && <BlockToggleButton tenant={tenant} user={u} />}
                      <DirectorToggleButton tenant={tenant} user={u} />
                      {!u.activated_at && (
                        <button onClick={() => setExtending(u)}
                          className="text-xs font-medium text-brand-cyan-text hover:text-brand-midnight hover:bg-blue-50 px-2 py-1.5 rounded-lg transition-colors">
                          Produži link
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Section C — Nalozi */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-3">
        <h2 className="text-sm font-semibold text-gray-900 mb-4">Nalozi</h2>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => setProvisioning(true)}
            className="flex items-center gap-2 bg-brand-cyan hover:bg-brand-cyan-dark text-brand-midnight text-sm font-medium px-4 py-2.5 rounded-lg">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Provajdovati naloge
          </button>

          {pending.length > 0 && (
            <button
              onClick={() => {
                const url = operatorApi.qrBundleUrl(tenant.id, token)
                const a = document.createElement('a')
                a.href = url; a.target = '_blank'; a.rel = 'noreferrer'
                document.body.appendChild(a); a.click(); a.remove()
              }}
              className="flex items-center gap-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium px-4 py-2.5 rounded-lg"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              QR PDF ({pending.length} na čekanju)
            </button>
          )}

          <button onClick={() => refetchUsers()}
            className="flex items-center gap-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-600 text-sm font-medium px-4 py-2.5 rounded-lg">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Osvežiti korisnike
          </button>
        </div>
      </div>

      {/* CSV Import */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-1">Uvoz podataka</h2>
        <p className="text-xs text-gray-500 mb-4">Inicijalno punjenje sistema CSV fajlovima. Redovi koji već postoje biće preskočeni bez greške.</p>
        <div className="space-y-3">
          <CsvImportCard
            title="Korisnici"
            description="Kreira naloge i aktivacione tokene. Redovi bez email-a se preskačaju."
            templateFile="users_template.csv"
            onImport={(f) => operatorApi.importUsers(token, tenant.id, f)}
            showAccounts
          />
          <CsvImportCard
            title="Vodomeri"
            description="subscriber_name i meter_serial su obavezni. Duplikati meter_serial se preskačaju."
            templateFile="meters_template.csv"
            onImport={(f) => operatorApi.importMeters(token, tenant.id, f)}
          />
          <CsvImportCard
            title="SCADA uređaji"
            description="SCADA uređaji. node_type mora biti: pump, reservoir, flow_meter, pressure_sensor ili level_sensor."
            templateFile="scada_template.csv"
            onImport={(f) => operatorApi.importScada(token, tenant.id, f)}
          />
        </div>
      </div>

      {/* Section — SCADA (pragovi upozorenja + edge čvorovi) */}
      <AdminTenantScada tenant={tenant} />

      {/* Section — deploy-tier tooling (tier indicator, shell terminal, deploy logs) */}
      <AdminEdgeDeployTools tenant={tenant} />

      {/* Delete zone */}
      <div className="bg-white border border-red-200 rounded-xl p-6">
        <h2 className="text-sm font-semibold text-red-700 mb-1">Opasna zona</h2>
        <p className="text-xs text-gray-500 mb-4">Brisanje tenanta je nepovratna akcija. Svi podaci, shema baze i nalozi biće trajno uništeni.</p>
        <button onClick={() => setConfirmDelete(true)}
          className="flex items-center gap-2 bg-red-50 hover:bg-red-100 border border-red-200 text-red-700 hover:text-red-800 text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
          Obrisati tenant
        </button>
      </div>

      {/* Modals */}
      {editing      && <EditTenantModal    tenant={tenant} onClose={() => setEditing(false)} />}
      {provisioning && <ProvisionModal     tenant={tenant} onClose={() => { setProvisioning(false); refetchUsers() }} />}
      {resetting    && <ResetPasswordModal tenant={tenant} user={resetting}  onClose={() => setResetting(null)} />}
      {extending    && <ExtendTokenDialog  tenant={tenant} user={extending}  onClose={() => setExtending(null)} />}
      {viewingDevices && <DevicesModal     tenant={tenant} user={viewingDevices} onClose={() => setViewingDevices(null)} />}
      {confirmDelete && <DeleteTenantModal tenant={tenant} onClose={() => setConfirmDelete(false)} />}
    </div>
  )
}
